import { useEffect, useState } from 'react';
import { UilCheck, UilTimes } from '@iconscout/react-unicons';

interface PasswordCriteriaProps {
  password: string;
}

interface CriteriaItem {
  id: string;
  label: string;
  validator: (pwd: string) => boolean;
}

const CRITERIA: CriteriaItem[] = [
  {
    id: 'length',
    label: 'At least 8 characters',
    validator: (pwd) => pwd.length >= 8
  },
  {
    id: 'uppercase',
    label: 'At least one uppercase letter (A-Z)',
    validator: (pwd) => /[A-Z]/.test(pwd)
  },
  {
    id: 'lowercase',
    label: 'At least one lowercase letter (a-z)',
    validator: (pwd) => /[a-z]/.test(pwd)
  },
  {
    id: 'digit',
    label: 'At least one digit (0-9)',
    validator: (pwd) => /[0-9]/.test(pwd)
  },
  {
    id: 'special',
    label: 'At least one special character (!@#$%^&*)',
    validator: (pwd) => /[!@#$%^&*]/.test(pwd)
  }
];

const PasswordCriteria = ({ password }: PasswordCriteriaProps) => {
  const [criteria, setCriteria] = useState<Array<CriteriaItem & { met: boolean }>>(
    CRITERIA.map(c => ({ ...c, met: false }))
  );

  useEffect(() => {
    setCriteria(
      CRITERIA.map(c => ({
        ...c,
        met: c.validator(password)
      }))
    );
  }, [password]);

  const allMetCriteria = criteria.every(c => c.met);
  const metCount = criteria.filter(c => c.met).length;

  return (
    <div className="mt-2 mb-2">
      {/* Criteria Badges */}
      <div className="d-flex flex-wrap gap-2">
        {criteria.map(item => (
          <div
            key={item.id}
            className={`d-flex align-items-center gap-1 px-2 py-1 rounded-2 transition-all ${
              item.met
                ? 'bg-success bg-opacity-10 border border-success border-opacity-25'
                : 'bg-body-tertiary bg-opacity-50 border border-body-secondary border-opacity-25'
            }`}
            style={{ fontSize: '12px', cursor: 'default' }}
            title={item.label}
          >
            {item.met ? (
              <UilCheck
                size={13}
                className="text-success"
                style={{ minWidth: '13px', marginTop: '1px' }}
              />
            ) : (
              <UilTimes
                size={13}
                className="text-body-tertiary"
                style={{ minWidth: '13px', marginTop: '1px' }}
              />
            )}
            <span
              className={`fw-500 ${
                item.met ? 'text-success' : 'text-body-tertiary'
              }`}
            >
              {item.id === 'length' && '8+ chars'}
              {item.id === 'uppercase' && 'Uppercase'}
              {item.id === 'lowercase' && 'Lowercase'}
              {item.id === 'digit' && 'Number'}
              {item.id === 'special' && 'Special'}
            </span>
          </div>
        ))}
      </div>

      {/* Strength Message */}
      <div className="mt-2">
        {allMetCriteria && password.length > 0 ? (
          <div className="d-flex align-items-center gap-2 text-success fw-500 fs-9">
            <UilCheck size={16} className="text-success" />
            <span>Password strength: Strong</span>
          </div>
        ) : password.length > 0 ? (
          <div className="text-body-tertiary fs-9">
            {metCount} of {criteria.length} requirements met
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PasswordCriteria;
